if (!customElements.get('tarmac-wheel-upgrade')) {
  customElements.define(
    'tarmac-wheel-upgrade',
    class TarmacWheelUpgrade extends HTMLElement {
      constructor() {
        super();
        this.mode = 'standard';
        this.submitting = false;
        this.boundSubmit = this.handleSubmit.bind(this);
        this.boundNativeChange = this.handleNativeChange.bind(this);
        this.boundPageShow = this.resetSubmittingUi.bind(this);
      }

      connectedCallback() {
        try {
          this.config = JSON.parse(this.querySelector('[data-wheel-upgrade-config]')?.textContent || '{}');
        } catch (error) {
          console.error('De Tarmac-wielupgrade kon niet worden geladen.', error);
          return;
        }

        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.form = this.productInfo?.querySelector(`#product-form-${CSS.escape(this.sectionId)}`);
        this.standardChoice = this.querySelector('[data-wheel-upgrade-standard]');
        this.carbonChoice = this.querySelector('[data-wheel-upgrade-carbon]');
        this.detail = this.querySelector('[data-wheel-upgrade-detail]');
        if (!this.form || !this.standardChoice || !this.carbonChoice || !this.detail) return;

        this.standardChoice.addEventListener('change', () => this.selectMode('standard'));
        this.carbonChoice.addEventListener('change', () => this.selectMode('carbon'));
        this.form.addEventListener('submit', this.boundSubmit, true);
        this.productInfo.addEventListener('change', this.boundNativeChange);
        window.addEventListener('pageshow', this.boundPageShow);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
            if (data?.sectionId && data.sectionId !== this.sectionId) return;
            if (!data?.variant?.id) return;
            this.refresh(String(data.variant.id));
            window.setTimeout(() => this.refresh(String(data.variant.id)), 100);
          });
        }

        this.refresh(this.currentVariantId());
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.boundSubmit, true);
        this.productInfo?.removeEventListener('change', this.boundNativeChange);
        window.removeEventListener('pageshow', this.boundPageShow);
        this.variantChangeUnsubscriber?.();
      }

      currentVariantId() {
        return String(this.form?.querySelector('[name="id"]')?.value || '');
      }

      currentBundleVariant(baseVariantId = this.currentVariantId()) {
        return this.config?.variants?.[String(baseVariantId)] || null;
      }

      handleNativeChange(event) {
        if (!event.target.closest('variant-selects') && event.target.name !== 'id') return;
        window.setTimeout(() => this.refresh(this.currentVariantId()), 0);
        window.setTimeout(() => this.refresh(this.currentVariantId()), 150);
      }

      selectMode(mode) {
        this.mode = mode;
        this.standardChoice.checked = mode === 'standard';
        this.carbonChoice.checked = mode === 'carbon';
        this.refresh(this.currentVariantId());
      }

      refresh(baseVariantId) {
        const bundleVariant = this.currentBundleVariant(baseVariantId);
        const carbonAvailable = Boolean(bundleVariant?.id && bundleVariant?.available);
        this.carbonChoice.disabled = !carbonAvailable;
        const carbonLabel = this.querySelector(`label[for="${CSS.escape(this.carbonChoice.id)}"]`);
        if (carbonLabel) carbonLabel.title = carbonAvailable ? '' : 'Deze combinatie is momenteel niet leverbaar';

        if (this.mode === 'carbon' && !carbonAvailable) this.mode = 'standard';
        this.standardChoice.checked = this.mode === 'standard';
        this.carbonChoice.checked = this.mode === 'carbon';

        const unavailable = this.mode === 'carbon' && !carbonAvailable;
        this.detail.textContent = this.mode === 'carbon'
          ? `${this.config.carbonName}${unavailable ? ' — momenteel niet leverbaar' : ''}`
          : this.config.standardName;
        this.detail.dataset.unavailable = String(unavailable);
        this.renderPrice(this.mode === 'carbon' ? bundleVariant?.bundlePrice : bundleVariant?.standardPrice);
      }

      renderPrice(cents) {
        if (!Number(cents)) return;
        const priceNode = this.productInfo?.querySelector(`#price-${CSS.escape(this.sectionId)}`);
        if (!priceNode) return;
        const money = new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR',
        }).format(Number(cents) / 100);
        priceNode.innerHTML = `<div class="price price--large"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${money}</span></div></div></div>`;
      }

      promiseProperties(promise, baseVariantId) {
        if (!promise) return {};
        return {
          _2ws_promise_version: promise.version || '1',
          _2ws_promise_profile: promise.profile || 'bike',
          _2ws_promise_route: promise.route || '',
          _2ws_promise_prefix: promise.prefix || '',
          _2ws_promise_date: promise.date || '',
          _2ws_inventory_message: promise.message || '',
          _2ws_promise_dot: promise.dot || '',
          _2ws_backorder_hold: String(Boolean(promise.hold)),
          _2ws_supplier_date: promise.supplierDate || '',
          _2ws_supplier_source: promise.supplierSource || '',
          _2ws_own_offset_days: String(promise.ownOffsetDays || 0),
          _2ws_supplier_offset_days: String(promise.supplierOffsetDays || 0),
          _2ws_future_offset_days: String(promise.futureOffsetDays || 0),
          _2ws_own_stock_at_add: String(promise.ownStockAtAdd || 0),
          _2ws_shipping_group: 'ship_complete',
          _2ws_wheel_upgrade: 'THOR Legend 50',
          _2ws_source_variant: String(baseVariantId),
        };
      }

      lineProperties(formData, baseVariantId, bundleVariant) {
        const properties = {
          _2ws_wheel_upgrade: 'THOR Legend 50',
          _2ws_source_variant: String(baseVariantId),
        };
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)]$/);
          if (!match || typeof value !== 'string' || !value) continue;
          properties[match[1]] = value;
        }
        return {
          ...properties,
          ...this.promiseProperties(bundleVariant?.bikePromise, baseVariantId),
        };
      }

      resetSubmittingUi() {
        this.submitting = false;
        const productForm = this.form?.closest('product-form');
        const button = productForm?.querySelector('[type="submit"]');
        const spinner = productForm?.querySelector('.loading__spinner');
        button?.classList.remove('loading');
        if (!button?.disabled) button?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }

      async handleSubmit(event) {
        if (this.mode !== 'carbon' || this.submitting) return;
        event.preventDefault();
        event.stopImmediatePropagation();

        const baseVariantId = this.currentVariantId();
        const bundleVariant = this.currentBundleVariant(baseVariantId);
        const productForm = this.form.closest('product-form');
        const button = productForm?.querySelector('[type="submit"]');
        const spinner = productForm?.querySelector('.loading__spinner');
        if (!bundleVariant?.id || !bundleVariant.available) {
          productForm?.handleErrorMessage?.('Deze combinatie is momenteel niet leverbaar.');
          return;
        }
        if (button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;

        const formData = new FormData(this.form);
        const quantity = Math.max(1, Number.parseInt(formData.get('quantity') || '1', 10));
        const body = {
          items: [{
            id: Number(bundleVariant.id),
            quantity,
            properties: this.lineProperties(formData, baseVariantId, bundleVariant),
          }],
        };

        this.submitting = true;
        productForm?.handleErrorMessage?.();
        button?.setAttribute('aria-disabled', 'true');
        button?.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'De wielupgrade kon niet worden toegevoegd.');
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'tarmac-wheel-upgrade',
              productVariantId: bundleVariant.id,
              cartData: data,
            });
          }
          this.resetSubmittingUi();
          window.location.assign(window.routes?.cart_url || '/cart');
        } catch (error) {
          productForm?.handleErrorMessage?.(error.message);
          this.resetSubmittingUi();
        }
      }
    }
  );
}
